import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { midwayConfig } from './midwayConfig';

export interface MidwayUser {
  username: string;
  displayName: string;
  email?: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: MidwayUser | null;
  isAuthenticated: boolean;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const MOCK_USER: MidwayUser = {
  username: 'dev-user',
  displayName: 'Dev User',
  email: 'dev-user@amazon.com',
};

export function MidwayAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<MidwayUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function resolveUser() {
      // Local development: bypass Midway and use a mock identity.
      if (midwayConfig.devMode) {
        if (!cancelled) {
          setUser(MOCK_USER);
          setStatus('authenticated');
        }
        return;
      }

      // Production: ask the Midway-protected edge who the current user is.
      try {
        const response = await fetch(midwayConfig.userEndpoint, {
          credentials: 'include',
          headers: { Accept: 'application/json' },
        });

        if (!cancelled && response.ok) {
          const data = (await response.json()) as MidwayUser;
          setUser(data);
          setStatus('authenticated');
          return;
        }

        // 401/403 -> session missing or expired.
        if (!cancelled) {
          setUser(null);
          setStatus('unauthenticated');
        }
      } catch {
        if (!cancelled) {
          setUser(null);
          setStatus('unauthenticated');
        }
      }
    }

    void resolveUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(() => {
    // Full-page navigation to the ALI-protected /api/login, which runs the
    // Midway OIDC flow (setting the session cookie) then 302s back here.
    const ret = window.location.pathname + window.location.search;
    window.location.assign(`/api/login?return=${encodeURIComponent(ret)}`);
  }, []);

  const signOut = useCallback(() => {
    setUser(null);
    setStatus('unauthenticated');
    window.location.assign(midwayConfig.signOutUrl);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      isAuthenticated: status === 'authenticated',
      signIn,
      signOut,
    }),
    [status, user, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within a MidwayAuthProvider');
  }
  return ctx;
}
